import { findPublicDesignerBySlugAndPortfolioId } from "@/services/user/designer.server";
import { getPortfolioImage } from "@/utils/images.server";
import { Anchor, Box, Image, Modal, Text } from "@mantine/core";
import type { LoaderArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData, useNavigate, useParams } from "@remix-run/react";
import { notFound } from "remix-utils";
import invariant from "tiny-invariant";

export default function OurDesignerPortfolioEntry() {
	const loaderData = useLoaderData<typeof loader>();
	const params = useParams<"slug">();
	const navigate = useNavigate();

	return (
		<Modal
			opened
			size="xl"
			onClose={() => navigate(`/our-designers/${params.slug!}`)}
		>
			<Box mt="xl">
				<Image
					height={284}
					fit="contain"
					src={`${loaderData.image}?w=1536`}
					radius="md"
				/>
				<Box ta="center" my="xl">
					<Text>{loaderData.title}</Text>
					{loaderData.link && (
						<Anchor
							href={loaderData.link}
							target="_blank"
							rel="noreferrer noopener"
							color="blue"
							display="block"
							underline
							size="sm"
						>
							Visit external link for this portfolio entry
						</Anchor>
					)}
				</Box>
				<Text
					sx={{
						whiteSpace: "pre-wrap",
						overflowWrap: "break-word",
					}}
					size="sm"
				>
					{loaderData.description}
				</Text>
			</Box>
		</Modal>
	);
}

export async function loader({ params }: LoaderArgs) {
	invariant(params.slug, "Slug is required");
	invariant(params.portfolioId, "Portfolio ID is required");

	const designer = await findPublicDesignerBySlugAndPortfolioId(
		params.slug,
		params.portfolioId,
		{
			select: {
				portfolio: {
					where: {
						id: params.portfolioId,
					},
					take: 1,
				},
			},
		}
	);

	if (!designer) {
		throw notFound("Designer or portfolio entry not found");
	}

	const portfolioEntry = designer.portfolio[0];

	invariant(portfolioEntry, "Portfolio entry not found");

	return json({
		title: portfolioEntry.title,
		description: portfolioEntry.description,
		image: getPortfolioImage(portfolioEntry.image),
		link: portfolioEntry.link,
	});
}
